<template>
  <div class="flex flex-col items-center w-full gap-2">
    <div
      class="bg-white flex flex-col dark:bg-gray-800 rounded-xl w-full mx-auto p-8 portrait:px-4 py-10"
    >
      <!-- Header with back button -->
      <div class="flex items-center mb-6">
        <button
          @click="$emit('back')"
          class="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>
        <h2 class="text-2xl font-bold dark:text-white">
          {{ selectedPlan.name }}
        </h2>
      </div>

      <!-- Plan Details -->
      <div class="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
        <div
          class="flex justify-between items-start mb-4 portrait:flex-col-reverse"
        >
          <div>
            <h3 class="text-xl font-semibold dark:text-white">Plan Overview</h3>
            <p
              class="text-gray-700 dark:text-gray-300 mt-2 text-base leading-relaxed"
            >
              {{ selectedPlan.details }}
            </p>
          </div>
          <span
            class="bg-main-100 dark:bg-sub-900 text-main-700 dark:text-sub-200 px-3 py-1 rounded-full text-sm font-medium"
          >
            {{ selectedPlan.type }}
          </span>
        </div>

        <!-- Features Toggle Button -->
        <button
          @click="showFeatures = !showFeatures"
          class="w-full flex items-center justify-between py-3 text-main-600 dark:text-sub-500 font-medium"
        >
          <span class="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ showFeatures ? "Hide Plan Features" : "Show Plan Features" }}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 transition-transform duration-200"
            :class="{ 'transform rotate-180': showFeatures }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Features Content -->
        <div
          v-if="showFeatures"
          class="pt-4 border-t border-gray-200 dark:border-gray-600 mt-2 transition-all duration-300 ease-in-out"
        >
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <h4
                class="font-medium text-lg mb-3 dark:text-white flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-green-500 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                What's Included
              </h4>
              <ul class="space-y-3">
                <li
                  v-for="(feature, index) in selectedPlan.features"
                  :key="'feature-' + index"
                  class="flex items-start"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="text-gray-700 dark:text-gray-300 text-base">
                    {{ feature }}
                  </span>
                </li>
              </ul>
            </div>

            <div
              v-if="selectedPlan.nofeatures && selectedPlan.nofeatures.length"
            >
              <h4
                class="font-medium text-lg mb-3 dark:text-white flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-red-500 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
                Not Included
              </h4>
              <ul class="space-y-3">
                <li
                  v-for="(feature, index) in selectedPlan.nofeatures"
                  :key="'nofeature-' + index"
                  class="flex items-start"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="text-gray-500 dark:text-gray-400 text-base">
                    {{ feature }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Enhanced Wallet Information -->
        <div
          class="my-6 border-2 border-dashed border-main-300 dark:border-sub-700 bg-main-50 dark:bg-sub-900/30 rounded-xl p-5"
        >
          <div class="flex items-start">
            <div class="bg-main-100 dark:bg-sub-800 p-2 rounded-lg mr-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 text-main-600 dark:text-sub-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold dark:text-white mb-2">
                Subscription Information
              </h3>
              <p class="text-gray-700 dark:text-gray-300 mb-3">
                Current balance:
                <span class="font-bold text-xl text-main-600 dark:text-sub-400"
                  >₦{{
                    wallet.toLocaleString("en-NG", { minimumFractionDigits: 2 })
                  }}</span
                >
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                You'll need at least 50% of the plan price,
                <span class="font-bold"
                  >₦{{ minWalletRequired.toLocaleString() }}</span
                >
                to activate this plan. Your wallet will be charged daily.
              </p>
              <div
                class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-3 rounded-r mb-4"
              >
                <div class="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <p class="text-yellow-700 dark:text-yellow-300 text-sm">
                    <span class="font-bold">Bonus:</span> Get extra bonus when
                    you fund your wallet with ₦10,000 or more!
                  </p>
                </div>
              </div>
              <button
                @click="openFundWallet"
                class="w-full bg-gradient-to-r from-main-700 to-main-900 hover:from-main-600 hover:to-main-900 dark:from-sub-700 dark:to-sub-900 dark:hover:from-sub-600 dark:hover:to-sub-900 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all hover:shadow-lg"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path
                    fill-rule="evenodd"
                    d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                Fund Wallet Now
              </button>
            </div>
          </div>
        </div>

        <!-- Price Summary -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-xl px-6 mb-6">
          <h4 class="font-medium text-lg dark:text-white mb-4">
            Order Summary
          </h4>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-gray-600 dark:text-gray-300 text-base"
                >Monthly Plan Price:</span
              >
              <span class="font-medium dark:text-white text-base"
                >₦{{
                  selectedPlan.price.toLocaleString("en-NG", {
                    minimumFractionDigits: 2,
                  })
                }}</span
              >
            </div>
            <div
              class="flex justify-between items-center"
              v-if="selectedPlan.discount > 0"
            >
              <span class="text-gray-600 dark:text-gray-300 text-base"
                >Plan Discount:</span
              >
              <span class="text-green-500 text-base"
                >-₦{{
                  Math.floor(
                    selectedPlan.price * (selectedPlan.discount / 100)
                  ).toLocaleString()
                }}</span
              >
            </div>
            <div
              class="flex justify-between items-center"
              v-if="additionalDiscount > 0"
            >
              <span class="text-gray-600 dark:text-gray-300 text-base"
                >Promo Discount:</span
              >
              <span class="text-green-500 text-base"
                >-₦{{ additionalDiscount.toLocaleString() }}</span
              >
            </div>
          </div>
          <div
            class="flex justify-between items-center text-lg font-bold mt-4 pt-4 border-t border-gray-200 dark:border-gray-600"
          >
            <span class="dark:text-white">Total Monthly:</span>
            <span class="text-main-500 dark:text-sub-600"
              >₦{{ calculateTotal().toLocaleString() }}</span
            >
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">
            <span>
              <b>Note:</b> Your wallet will be debited daily for this plan.
            </span>
          </div>
        </div>

        <!-- Payment Options -->
        <div class="space-y-3 mb-6 px-6 pb-6">
          <h4 class="font-medium text-lg dark:text-white mb-3">
            Choose Payment Method
          </h4>
          <button
            @click="selectPayment('wallet')"
            :class="{
              'ring-2 ring-main-500 dark:ring-sub-600 bg-main-50 dark:bg-sub-900/20':
                paymentMethod === 'wallet',
            }"
            class="w-full flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            <div class="flex items-center">
              <div
                class="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg mr-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <p class="font-medium text-left dark:text-white text-base">
                  Pay with Wallet
                </p>
                <p class="text-gray-500 dark:text-gray-400 text-left text-sm">
                  Current balance: ₦{{
                    wallet.toLocaleString("en-NG", {
                      minimumFractionDigits: 2,
                    })
                  }}
                </p>
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- Proceed Button -->
        <button
          @click="processSubscription"
          class="w-full bg-gradient-to-r from-main-500 to-main-600 hover:from-main-600 hover:to-main-700 dark:from-sub-600 dark:to-sub-700 dark:hover:from-sub-700 dark:hover:to-sub-800 text-white py-4 px-4 rounded-lg mt-2 font-medium flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg text-lg"
          :disabled="
            processingSubscription ||
            wallet < minWalletRequired ||
            hasActivePlan
          "
          :class="{
            'opacity-50 cursor-not-allowed':
              wallet < selectedPlan.price ||
              processingSubscription ||
              hasActivePlan,
          }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clip-rule="evenodd"
            />
          </svg>
          {{
            processingSubscription
              ? "Processing..."
              : hasActivePlan
                ? "You already have an active plan"
                : wallet < minWalletRequired
                  ? "Insufficient Wallet Balance"
                  : "Activate Plan"
          }}
        </button>
      </div>
    </div>
    <ModalComponent v-model:dialogVisible="showModal">
      <template #modalContent>
        <component
          :is="currentModalComponent"
          v-bind="currentModalProps"
          @close="showModal = false"
        />
      </template>
    </ModalComponent>
  </div>
</template>

<script>
import ModalComponent from "@/components/modalComponent.vue";
import FundWalletComponent from "@/components/fundWalletComponent.vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
export default {
  props: {
    selectedPlan: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    console.log("Selected Plan:", this.selectedPlan);
  },
  components: {
    ModalComponent,
    FundWalletComponent,
  },
  data() {
    return {
      showModal: false,
      currentModalComponent: null,
      currentModalProps: {},
      paymentMethod: "wallet",
      processingSubscription: false,
      showOtherPaymentMethods: false,
      showFeatures: false, // Controls feature visibility
    };
  },
  computed: {
    minWalletRequired() {
      return this.selectedPlan.price * 0.5;
    },
    hasActivePlan() {
      return this.$store.getters.subscription?.status === "active";
    },
    wallet() {
      const walletBalance = Number(this.$store.getters.wallet?.balance ?? 0);
      const bonusBalance = Number(this.$store.getters.wallet?.bonus ?? 0);
      return walletBalance + bonusBalance;
    },
  },
  methods: {
    openFundWallet() {
      const amountNeeded = Math.max(this.selectedPlan.price - this.wallet, 0);
      this.currentModalComponent = FundWalletComponent;
      this.currentModalProps = { suggestedAmount: Math.ceil(amountNeeded) };
      this.showModal = true;
    },
    selectPayment(method) {
      this.paymentMethod = method;
    },
    calculateTotal() {
      let price = this.selectedPlan.price;
      if (this.selectedPlan.discount) {
        price -= price * (this.selectedPlan.discount / 100);
      }
      return price > 0 ? Math.round(price) : 0;
    },
    async processSubscription() {
      this.processingSubscription = true;
      try {
        const response = await this.$store.dispatch("setSubscription", {
          planId: this.selectedPlan.id,
          duration: 1,
          amount: Number(this.selectedPlan.price),
        });

        if (!response.success) {
          throw new Error("Subscription processing failed");
        }

        toast.success("✅ Subscription successfully activated!", {
          position: "top-right",
        });
      } catch (error) {
        toast.error("❌ Subscription failed. Please try again.", {
          position: "top-right",
        });
      } finally {
        this.processingSubscription = false;
      }
    },
  },
};
</script>
